import { ORDER_SOURCE_VALUES, ORDER_VIEW_VALUES } from "@erp/shared";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, CircleCheck, Columns3, Flame, List, PackageOpen, Plus, Truck } from "lucide-react";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";
import { useCallback } from "react";
import { z } from "zod";
import { Badge } from "~/components/ui/badge";
import { Button, buttonVariants } from "~/components/ui/button";
import { DataList, type Column } from "~/components/ui/data-list";
import { Page, PageHeader, Panel, SearchInput, Toolbar } from "~/components/ui/page";
import { Select } from "~/components/ui/select";
import { EmptyState, ErrorState } from "~/components/ui/states";
import { PreparationBoard } from "~/features/orders/board";
import { OrderFlags as Flags } from "~/features/orders/flags";
import { ORDER_TONE, PAYMENT_TONE } from "~/features/sales/status";
import { api, unwrap } from "~/lib/api";
import { downloadLaposteFile } from "~/features/orders/laposte-export";
import { cn } from "~/lib/cn";
import { errorMessageKey } from "~/lib/errors";
import { useFormat, useT } from "~/lib/i18n";
import { useCan } from "~/lib/session";
import { useInfiniteList } from "~/lib/use-list";
import { HelpTip } from "~/components/ui/help-tip";

const search = z.object({
  q: z.string().optional(),
  view: z.enum(ORDER_VIEW_VALUES).optional(),
  source: z.enum(ORDER_SOURCE_VALUES).optional(),
  flag: z.enum(["urgent", "ready", "stockout", "remainder"]).optional(),
  layout: z.enum(["list", "board"]).optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute("/_app/orders/")({ validateSearch: search, component: Orders });

function fetchOrders(params: Search & { offset: number; limit: number }) {
  return unwrap(api.orders.get({ query: { ...params, view: params.view ?? "open" } }));
}
type Row = Awaited<ReturnType<typeof fetchOrders>>["items"][number];

const VIEWS = ["open", "to_process", "preparing", "shipped", "delivered", "to_invoice", "archived"] as const;
const ALL = "__all";

const FLAG_ICONS = { urgent: Flame, ready: CircleCheck, stockout: AlertTriangle, remainder: PackageOpen } as const;

function Orders() {
  const t = useT();
  const format = useFormat();
  const can = useCan();
  const navigate = useNavigate({ from: Route.fullPath });
  const filters = Route.useSearch();
  const view = filters.view ?? "open";
  const board = filters.layout === "board";
  const { layout: _layout, ...listFilters } = filters;
  const list = useInfiniteList("orders", listFilters, fetchOrders);
  const counts: Record<string, number> = list.firstPage?.counts ?? {};
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );

  // The La Poste file covers the orders of this list that still have something to ship: all of
  // them, not only the rows scrolled into view (the list loads 100 at a time).
  const shippable = (r: Row) => r.status === "to_process" || r.status === "preparing";
  const toShip = (list.rows as Row[]).filter(shippable);
  const laposte = useMutation({
    mutationFn: async () => {
      const all: Row[] = [];
      for (let offset = 0; ; offset += 500) {
        const page = await fetchOrders({ ...listFilters, offset, limit: 500 });
        all.push(...page.items);
        if (page.items.length === 0 || all.length >= page.total) break;
      }
      const ids = all.filter(shippable).map((r) => r.id);
      if (ids.length === 0) return void toast.error(t("shipments.laposte.nothing"));
      await downloadLaposteFile(t, ids);
    },
    onError: (e) => toast.error(t(errorMessageKey(e))),
  });

  const columns: Column<Row>[] = [
    {
      id: "number",
      header: t("quotes.fields.number"),
      cell: (r) => (
        <div className="py-1.5">
          <p className="font-medium tabular">{r.number}</p>
          <p className="text-xs text-fg-3">{t(`orders.sources.${r.source}`)}</p>
        </div>
      ),
    },
    {
      id: "customer",
      header: t("orders.fields.customer"),
      cell: (r) => (
        <div className="min-w-0">
          <p className="truncate">{r.customerName}</p>
          {r.externalReference ? <p className="truncate text-xs text-fg-3">{r.externalReference}</p> : null}
        </div>
      ),
      className: "max-w-[280px]",
    },
    { id: "date", header: t("orders.fields.orderedAt"), cell: (r) => format.date(r.orderedAt, "short"), className: "text-fg-2" },
    { id: "flags", header: "", cell: (r) => <Flags row={r} /> },
    { id: "items", header: t("orders.fields.items"), align: "end", cell: (r) => format.number(r.itemCount), minWidth: "xl" },
    { id: "total", header: t("orders.fields.total"), align: "end", cell: (r) => format.money(r.grossCents) },
    {
      id: "payment",
      header: t("orders.fields.payment"),
      cell: (r) => <Badge tone={PAYMENT_TONE[r.paymentStatus]}>{t(`orders.payment.${r.paymentStatus}`)}</Badge>,
      minWidth: "lg",
    },
    {
      id: "status",
      header: t("common.fields.status"),
      cell: (r) => <Badge tone={ORDER_TONE[r.status]}>{t(`orders.statuses.${r.status}`)}</Badge>,
    },
  ];

  const chip = (active: boolean) =>
    cn(
      "flex h-9 shrink-0 items-center gap-2 rounded-full border px-3.5 text-sm font-medium transition-colors",
      active
        ? "border-ink bg-ink text-white dark:border-gold dark:bg-gold dark:text-gold-ink"
        : "border-line-strong bg-surface text-fg-2 hover:text-fg",
    );

  return (
    <Page>
      <PageHeader
        title={t("orders.title")}
        actions={
          <>
            <fieldset
              className="flex rounded-control border border-line p-0.5 max-md:hidden"
              aria-label={t("orders.board.layout")}
            >
              {(["list", "board"] as const).map((layout) => (
                <button
                  key={layout}
                  type="button"
                  aria-pressed={(filters.layout ?? "list") === layout}
                  aria-label={t(`orders.board.${layout}`)}
                  title={t(`orders.board.${layout}`)}
                  onClick={() => setSearch({ layout: layout === "list" ? undefined : layout })}
                  className={cn(
                    "grid size-9 place-items-center rounded-[6px] text-fg-3 [&_svg]:size-4",
                    (filters.layout ?? "list") === layout ? "bg-sunken text-fg" : "hover:text-fg",
                  )}
                >
                  {layout === "list" ? <List /> : <Columns3 />}
                </button>
              ))}
            </fieldset>
            {can("sales:write") ? (
              <Link to="/orders/new" className={buttonVariants({ variant: "primary" })}>
                <Plus />
                {t("orders.new")}
              </Link>
            ) : null}
          </>
        }
      />
      {board ? (
        <div className="max-md:hidden">
          <PreparationBoard q={filters.q} source={filters.source} />
        </div>
      ) : null}
      <div className={cn(board && "md:hidden")}>
        <div className="no-scrollbar mb-3 flex gap-2 overflow-x-auto">
          {VIEWS.map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={view === v}
              onClick={() => setSearch({ view: v === "open" ? undefined : v, flag: undefined })}
              className={chip(view === v)}
            >
              {t(`orders.views.${v}`)}
              {counts[v] !== undefined ? <span className="text-xs tabular opacity-70">{format.number(counts[v])}</span> : null}
            </button>
          ))}
          <HelpTip id="statuses.all" className="self-center" />
        </div>
        <Panel>
          <Toolbar>
            <SearchInput
              value={filters.q ?? ""}
              onChange={(q) => setSearch({ q: q || undefined })}
              placeholder={t("orders.searchPlaceholder")}
            />
            {can("stock:write") && (view === "open" || view === "to_process" || view === "preparing") ? (
              <span className="inline-flex items-center gap-0.5">
                <Button
                  variant="secondary"
                  onClick={() => (toShip.length || list.hasMore ? laposte.mutate() : toast.error(t("shipments.laposte.nothing")))}
                  loading={laposte.isPending}
                  title={t("shipments.laposte.hint")}
                >
                  <Truck />
                  {list.hasMore ? t("shipments.laposte.action") : t("shipments.laposte.actionCount", { count: toShip.length })}
                </Button>
                <HelpTip id="shipment.laposteFile" />
              </span>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {(["urgent", "ready", "stockout", "remainder"] as const).map((flag) => {
                const Icon = FLAG_ICONS[flag];
                return (
                  <button
                    key={flag}
                    type="button"
                    aria-pressed={filters.flag === flag}
                    onClick={() => setSearch({ flag: filters.flag === flag ? undefined : flag })}
                    className={cn(chip(filters.flag === flag), "h-9")}
                  >
                    <Icon className="size-4" aria-hidden />
                    {t(`orders.flags.${flag}`)}
                    {counts[flag] ? <span className="text-xs tabular opacity-70">{format.number(counts[flag])}</span> : null}
                  </button>
                );
              })}
            </div>
            <div className="w-full sm:ml-auto sm:w-44">
              <Select
                aria-label={t("orders.fields.source")}
                value={filters.source ?? ALL}
                onValueChange={(v) => setSearch({ source: v === ALL ? undefined : (v as Search["source"]) })}
                options={[
                  { value: ALL, label: t("orders.views.all") },
                  ...ORDER_SOURCE_VALUES.map((s) => ({ value: s, label: t(`orders.sources.${s}`) })),
                ]}
              />
            </div>
          </Toolbar>
          {list.isError ? (
            <ErrorState error={list.error} onRetry={() => void list.refetch()} />
          ) : (
            <DataList
              rows={list.rows}
              rowKey={(r) => r.id}
              columns={columns}
              card={(r) => (
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{r.customerName}</p>
                    <p className="text-xs text-fg-3 tabular">
                      {r.number} — {format.date(r.orderedAt, "short")} — {t(`orders.sources.${r.source}`)}
                    </p>
                    <div className="mt-1.5">
                      <Flags row={r} />
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="tabular">{format.money(r.grossCents)}</p>
                    <Badge tone={ORDER_TONE[r.status]}>{t(`orders.statuses.${r.status}`)}</Badge>
                  </div>
                </div>
              )}
              onOpen={(r) => void navigate({ to: "/orders/$id", params: { id: r.id } })}
              sort={{ dir: "asc" }}
              onSortChange={() => undefined}
              loading={list.isLoading}
              hasMore={list.hasMore}
              loadingMore={list.loadingMore}
              onLoadMore={list.loadMore}
              isDimmed={(r) => r.status === "cancelled"}
              empty={
                view === "open" && !filters.q && !filters.flag && !filters.source ? (
                  <EmptyState icon={<CheckCircle2 />} title={t("orders.allDone")} body={t("orders.allDoneBody")} />
                ) : (
                  <EmptyState title={t("common.states.noResults")} body={t("common.states.noResultsHint")} />
                )
              }
            />
          )}
        </Panel>
      </div>
    </Page>
  );
}
